// Product-name field with a live-filtered dropdown of curated commercial
// pesticides. Picking a suggestion auto-fills the EPA reg # on the parent
// form (via onSelect), so techs never type an EPA number wrong. Free-text
// entry is still allowed — the list is a shortcut, not a straitjacket.
import { useEffect, useRef, useState } from "react";
import { matchPesticides } from "@/data/pesticides";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function PesticideAutocomplete({
  value,
  onChange,
  onSelect,   // ({ name, epa_reg, active, targets, form }) => void
  className,
  placeholder = "Start typing… Advion, Termidor, Contrac",
  testid = "pest-product",
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const suggestions = matchPesticides(value, 8);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (p) => {
    onChange(p.name);
    onSelect?.(p);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && suggestions[hi]) { e.preventDefault(); pick(suggestions[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testid}-wrap`}>
      <div className="relative">
        <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
        <input
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          data-testid={testid}
          autoComplete="off"
          className={className ? `${className} pl-9` : "w-full h-11 pl-9 pr-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"}
        />
      </div>

      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute z-30 left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto bg-white border-2 border-slate-200 rounded-lg shadow-xl"
        >
          {suggestions.map((p, i) => (
            <button
              key={`${p.name}-${p.epa_reg}`}
              type="button"
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(p)}
              data-testid={`${testid}-opt-${i}`}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 ${i === hi ? "bg-emerald-50" : "hover:bg-slate-50"}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-sm text-slate-900">{p.name}</span>
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{p.brand}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                <span className="font-mono">EPA {p.epa_reg}</span>
                <span className="mx-1.5">·</span>
                <span className="italic">{p.active}</span>
              </div>
              <div className="mt-1 flex items-center gap-1 flex-wrap">
                {p.targets.map((t) => (
                  <span key={t} className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                    {t}
                  </span>
                ))}
                <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  {p.form}
                </span>
              </div>
            </button>
          ))}
          <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 font-medium">
            Curated list. Free-typing is fine — always verify the EPA # on the physical label.
          </div>
        </div>
      )}
    </div>
  );
}
